import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { createGPContact } from "../../../api/services/Client";
import { useParams } from "react-router-dom";
import CustomInput from "../../CustomForm/CustomInput";

const GPModal = ({ show, onHide }) => {
  const [formData, setFormData] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const mutation = useMutation(createGPContact, {
    onSuccess: () => {
      enqueueSnackbar("GP Contact created successfully", {
        variant: "success",
      });
      queryClient.invalidateQueries("gpContacts");
      setFormData({});
      onHide();
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to create GP Contact",
        {
          variant: "error",
        }
      );
    },
  });

  const handleSave = () => {
    const inputData = new FormData();
    for (const key in formData) {
      if (formData[key] !== undefined && formData[key] !== "") {
        inputData.append(key, formData[key]);
      }
    }
    inputData.append("client", clientID);
    mutation.mutate(inputData);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClose = () => {
    setFormData({});
    onHide();
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflowY: "visible",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          overflowY: "visible",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EFF8FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonAddIcon sx={{ color: "#2469F0", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ color: "#1A1A2E" }}
              >
                Add GP Contact
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Add a new GP contact for the client
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, overflowY: "visible" }}>
        {/* 🔹 3 fields per row on desktop, stacks on smaller screens */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="GP First Name *"
              name="first_name"
              placeholder="Enter first name"
              value={formData.first_name || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="GP Last Name *"
              name="last_name"
              placeholder="Enter last name"
              value={formData.last_name || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Telephone *"
              name="telephone"
              placeholder="Primary number"
              value={formData.telephone || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Mobile"
              name="mobile"
              placeholder="Mobile number"
              value={formData.mobile || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Email"
              name="email"
              placeholder="Email address"
              value={formData.email || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="GP Practice Name *"
              name="gp_practice_name"
              placeholder="Enter practice name"
              value={formData.gp_practice_name || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="GP Practice Identifier"
              name="gp_practice_identifier"
              placeholder="Enter practice identifier"
              value={formData.gp_practice_identifier || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={mutation.isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={mutation.isLoading}
          startIcon={
            mutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 3,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {mutation.isLoading ? "Saving..." : "Save GP Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GPModal;